import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { buildSectionCommitFormData } from '@src/shared/lib';
import { useMutation } from '@src/shared/query';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { clientGroupDetailFromDto } from '../../lib';
import type {
  BusinessSegment,
  ClientCompetitorOption,
  ClientGroupDetail,
  ClientGroupDetailDto,
  CommitClientGroupDetailPayload,
  CommitClientGroupDetailResponse,
} from '../../types';
import { clientCompetitorsUrl, clientGroupUrl } from './constants';
import { CLIENTS_KEY, clientGroupDetailKey, invalidateClients } from './keys';

function clientGroupDetailQueryOptions(
  apiClient: APIClient,
  id: number | string,
) {
  return queryOptions({
    queryKey: clientGroupDetailKey(id),
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientGroupUrl(id),
        options: { signal },
      }) as Promise<ClientGroupDetailDto>,
  });
}

/** Header and sections of the client group page. */
export function useClientGroupDetail(
  id: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    ...clientGroupDetailQueryOptions(apiClient, id),
    enabled,
    select: (data): ClientGroupDetail => clientGroupDetailFromDto(data),
  });
}

/**
 * The client group outside React, e.g. in a route loader. Shares the cache
 * with `useClientGroupDetail`.
 */
export async function fetchClientGroupDetail(
  apiClient: APIClient,
  id: number | string,
): Promise<ClientGroupDetail> {
  const data = await queryClient.ensureQueryData(
    clientGroupDetailQueryOptions(apiClient, id),
  );
  return clientGroupDetailFromDto(data);
}

/** Saves the given sections of the client group page together. */
export function useCommitClientGroupDetail() {
  return useMutation<
    CommitClientGroupDetailResponse,
    Error,
    { id: number | string; sections: CommitClientGroupDetailPayload }
  >({
    mutationKey: [...CLIENTS_KEY, 'groups', 'detail', 'commit'],
    mutateFn: ({ apiClient, id, sections }) =>
      apiClient.request({
        url: `${clientGroupUrl(id)}detail/commit/`,
        method: 'PATCH',
        payload: buildSectionCommitFormData(sections),
        contentType: 'multipart/form-data',
      }) as Promise<CommitClientGroupDetailResponse>,
    onSuccess: (response, variables) => {
      // Every saved section answers with the whole group; it goes into the
      // cache at once, so the page does not show the old one meanwhile.
      const saved = Object.values(response.results).find(Boolean);
      if (saved) {
        queryClient.setQueryData(clientGroupDetailKey(variables.id), saved);
      }
      void invalidateClients();
    },
  });
}

/**
 * The verification button of an Entities section: the user verifies the
 * active clients of the group in the business segment.
 */
export function useVerifyClientGroupSegment() {
  return useMutation<
    ClientGroupDetailDto,
    Error,
    { id: number | string; segment: BusinessSegment }
  >({
    mutationKey: [...CLIENTS_KEY, 'groups', 'detail', 'verify'],
    mutateFn: ({ apiClient, id, segment }) =>
      apiClient.request({
        url: `${clientGroupUrl(id)}verify/`,
        method: 'POST',
        payload: { segment },
      }) as Promise<ClientGroupDetailDto>,
    onSuccess: (response, variables) => {
      queryClient.setQueryData(clientGroupDetailKey(variables.id), response);
      void invalidateClients();
    },
  });
}

/** Active suppliers marked as competitors, for the Competition section. */
export function useClientCompetitors({ enabled = true } = {}) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'groups', 'competitors'],
    enabled,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientCompetitorsUrl,
        options: { signal },
      }) as Promise<ClientCompetitorOption[]>,
  });
}
